<template>
  <div class="relative min-h-screen scroll-smooth bg-black text-white">
    <TheNavbar />

    <section class="relative flex min-h-screen items-center justify-center overflow-hidden px-4 text-center sm:px-6">
      <video autoplay loop muted playsinline class="absolute left-0 top-0 z-0 h-full w-full object-cover">
        <source src="/videos/sitevideo.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <div class="relative z-20 max-w-3xl">
        <h1
          class="mb-6 text-3xl font-semibold leading-tight sm:mb-9 sm:text-4xl sm:leading-none md:text-5xl lg:text-6xl"
        >
          Soundtrack Your Videos with Curated Music from AMG — Free for Creators
        </h1>
        <p class="text-primary mx-auto mt-6 max-w-sm font-semibold leading-tight sm:mt-9 sm:max-w-md sm:text-lg">
          No takedowns. No strikes. Just credit the artist and let AMG monetize your video.
        </p>
      </div>
    </section>

    <!-- Value Proposition Section -->
    <section id="why-its-free" class="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:py-36">
      <div class="mb-8">
        <h2 class="text-2xl font-semibold leading-tight sm:text-3xl md:text-4xl lg:text-6xl">
          <span
            class="text-primary inline-block pb-1 align-middle text-base uppercase tracking-wide sm:text-lg lg:text-xl"
          >
            Why it's free
          </span>
          This is part of our artist development strategy. Rather than block or charge up front, we monetize through
          YouTube's Content ID system. This lets us pay our artists while helping creators grow their content with great
          music.
        </h2>
      </div>
    </section>

    <!-- How It Works Section -->
    <section
      id="how-it-works"
      class="mx-auto flex min-h-[50vh] max-w-6xl flex-col justify-center px-4 pt-8 sm:min-h-[65vh] sm:px-6 sm:pt-16"
    >
      <div class="mb-8 sm:mb-12">
        <span class="text-primary text-lg font-bold uppercase tracking-wide sm:text-xl">How it works</span>
      </div>

      <div
        class="flex flex-col space-y-8 md:flex-row md:flex-nowrap md:items-center md:justify-between md:gap-4 md:space-y-0"
      >
        <template v-for="(step, index) in howItWorksSteps" :key="step.title">
          <div class="flex-1 text-left md:max-w-40">
            <div class="mb-2 text-sm font-medium text-gray-400 md:hidden">Step {{ index + 1 }}</div>
            <h3 class="text-lg font-bold leading-tight sm:text-xl md:text-base">{{ step.title }}</h3>
          </div>
          <div v-if="index < howItWorksSteps.length - 1" class="hidden flex-shrink-0 md:flex">
            <img src="/icons/arrow-right.svg" alt="Arrow" class="h-4 w-4 md:h-6 md:w-6" />
          </div>
        </template>
      </div>
    </section>

    <!-- Application Section -->
    <section class="mx-auto max-w-6xl px-4 py-12 text-center sm:px-6 sm:py-16">
      <h2 class="text-primary text-lg font-bold uppercase tracking-wide sm:text-xl">
        Ready To Use Amg Music In Your Videos?
      </h2>
      <p class="mx-auto my-6 mb-8 max-w-4xl text-lg sm:mb-12 sm:text-xl">
        We're limiting a limited number of creators to access our curated catalog of free-use music. Tell us about your
        channel, and we'll get back to you with access details.
      </p>
      <NuxtLink
        to="https://tally.so/r/mRQaLp"
        external
        class="bg-primary hover:bg-primary/80 focus:ring-primary/75 rounded-lg px-5 py-2.5 text-xs font-semibold uppercase shadow-md transition-all duration-200 focus:ring-2 sm:px-7 sm:py-3 sm:text-sm"
        aria-label="Apply for free music access"
      >
        Apply Now
      </NuxtLink>
    </section>

    <!-- Commercial Rights Section -->
    <section id="commercial-rights" class="mx-auto max-w-6xl px-4 py-24 text-center sm:px-6 sm:py-32 lg:py-48">
      <div class="mb-8">
        <h2 class="text-primary text-lg font-bold uppercase tracking-wide sm:text-xl">Want Commercial Rights?</h2>
        <p class="mt-2 text-xl sm:text-2xl lg:text-3xl">
          For commercial, brand, or business use:<br />
          Contact
          <NuxtLink
            to="mailto:licensing@amgrecordings.com"
            external
            class="text-primary hover:text-primary/80 underline"
          >
            licensing@amgrecordings.com
          </NuxtLink>
        </p>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-16">
      <div class="mb-8 text-center sm:mb-12">
        <h2 class="text-primary text-lg font-bold uppercase tracking-wide sm:text-xl">Frequently Asked Questions</h2>
        <h3 class="mt-3 text-base font-semibold sm:text-lg">Everything you need to know about the program.</h3>
      </div>

      <div class="space-y-4">
        <details v-for="faq in faqs" :key="faq.question" class="group">
          <summary
            class="flex cursor-pointer items-center justify-between py-3 text-lg hover:text-gray-300 sm:py-4 sm:text-xl"
          >
            <span class="font-semibold">{{ faq.question }}</span>
            <span class="ml-2 transform transition-transform group-open:rotate-180">⌄</span>
          </summary>
          <div class="pb-3 text-sm text-gray-400 sm:pb-4 sm:text-base">{{ faq.answer }}</div>
        </details>
      </div>
    </section>

    <!-- Footer -->
    <footer class="px-4 py-12 text-center sm:px-6 sm:py-16">
      <div
        class="mb-6 flex flex-wrap justify-center gap-4 py-8 text-xs font-bold text-gray-400 sm:mb-8 sm:gap-8 sm:py-16 sm:text-sm"
      >
        <NuxtLink
          v-for="link in socialLinks"
          :key="link.name"
          :to="link.url"
          external
          class="uppercase hover:text-white"
        >
          {{ link.name }}
        </NuxtLink>
      </div>
      <div class="mb-6 sm:mb-8">
        <NuxtLink to="https://aresmusicgroup.com" external class="block transition-all duration-300">
          <img
            src="/img/logo-horizontal.svg"
            alt="Ares Music Group"
            class="mx-auto h-6 opacity-50 transition-all duration-300 hover:opacity-100 sm:h-8"
          />
        </NuxtLink>
      </div>
      <div class="text-xs text-gray-500 sm:text-sm">
        &copy; {{ new Date().getFullYear() }} Ares Music Group Recordings, LLC. All Rights Reserved.
      </div>
    </footer>

    <!-- Back to Top Button -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 scale-75 translate-y-4"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-75 translate-y-4"
    >
      <button
        v-if="showBackToTop"
        class="bg-primary hover:bg-primary/90 fixed bottom-4 right-4 z-50 cursor-pointer rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110 sm:bottom-6 sm:right-6 sm:p-3"
        aria-label="Back to top"
        @click="scrollToTop"
      >
        <svg class="h-5 w-5 sm:h-6 sm:w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
const { showBackToTop, scrollToTop } = useScrollToTop();

const howItWorksSteps = [
  {
    title: 'Browse our curated catalog of music',
  },
  {
    title: 'Add the track to your YouTube Video',
  },
  {
    title: 'Credit the artist and AMG in your video description',
  },
  {
    title: 'Let us handle the monetization via Content ID',
  },
];

const socialLinks = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/amgrecordings/',
  },
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/aresmusicgroup/',
  },
  {
    name: 'Twitter',
    url: 'https://x.com/aresmusicgroup',
  },
  {
    name: 'Contact us',
    url: 'mailto:info@amgrecordings.com',
  },
];

const faqs = [
  {
    question: 'Is it really free to use your music?',
    answer:
      "Yes — if your application is approved, you can use selected tracks in your YouTube videos for free. In exchange, we monetize those videos via YouTube's Content ID system.",
  },
  {
    question: 'Will I get a copyright claim on my video?',
    answer:
      "Yes, but don't worry. It's not a strike — it's just a Content ID claim. AMG will monetize your video, but you can still earn revenue from your content (depending on your YouTube settings).",
  },
  {
    question: 'Do I need to credit the artist or AMG?',
    answer:
      "Yes. Every video must include a credit line in the description. You'll get the exact format once approved.",
  },
  {
    question: 'What kind of videos are allowed?',
    answer:
      "Vlogs, commentary, tutorials, short films, animations — anything non-commercial. For brand deals, ads, or commercial use, you'll need a separate license.",
  },
  {
    question: 'Can I use your music on other platforms like Instagram or TikTok?',
    answer: 'Yes!',
  },
  {
    question: 'Can I monetize my video too?',
    answer:
      "Yes, but AMG will collect a portion of the revenue through Content ID. If you're part of YouTube's Partner Program, both you and AMG may earn.",
  },
  {
    question: 'What if I stop using the music — will the claim go away?',
    answer:
      'No — the claim stays as long as the music is in the video. If you remove the music, the claim will be released.',
  },
  {
    question: 'How do I get access to the music?',
    answer:
      "Once approved, you'll get a private link to stream and download tracks via DISCO. Access is managed manually, so approval is required.",
  },
];
</script>
